<template>
  <div class="order-list">
    <h2>我的订单</h2>
    <div class="after-sales-entry">
      <button class="btn btn-secondary" @click="$router.push('/after-sales')">查看售后记录</button>
    </div>
    
    <!-- 筛选条件 -->
    <div class="filter-section">
      <div class="filter-item">
        <label for="status-filter">订单状态：</label>
        <select id="status-filter" v-model="statusFilter" @change="fetchOrders">
          <option value="">全部状态</option>
          <option value="0">客户下单</option>
          <option value="1">商家确认</option>
          <option value="2">备货完成</option>
          <option value="3">开始发货</option>
          <option value="4">交易完成</option>
          <option value="5">交易失败</option>
        </select>
      </div>
      <div class="filter-item">
        <label for="time-filter">时间排序：</label>
        <select id="time-filter" v-model="timeFilter" @change="fetchOrders">
          <option value="desc">最新优先</option>
          <option value="asc">最早优先</option>
        </select>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading">加载中...</div>

    <!-- 错误提示 -->
    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <!-- 订单列表 -->
    <div v-if="orders.length > 0" class="orders-container">
      <div v-for="order in orders" :key="order.id" class="order-card card">
        <div class="order-header">
          <span class="order-id">订单号: {{ order.id }}</span>
          <span class="order-time">{{ formatDate(order.created_at) }}</span>
        </div>
        
        <div class="order-content">
          <div class="products-info">
            <div v-for="(item, index) in order.items || [{productName: order.product_name, price: order.price, quantity: order.quantity}]" :key="index" class="product-row">
              <img
                :src="getOrderItemImageUrl(item, index)"
                :alt="item.productName || item.name || '商品图片'"
                class="product-thumb"
                referrerpolicy="no-referrer"
              >
              <div class="product-copy">
                <span class="product-name">{{ item.productName || item.name || '未知商品' }}</span>
                <span v-if="item.sizeName" class="product-size">{{ item.sizeName }}<span v-if="item.sizeExtraPrice > 0">(+¥{{ item.sizeExtraPrice.toFixed(2) }})</span></span>
              </div>
              <span class="product-price">¥{{ formatPrice(item.price || 0) }}</span>
              <span class="product-quantity">×{{ item.quantity || 1 }}</span>
            </div>
            <div v-if="(order.items && order.items.length > 1)" class="more-items">
              等{{ order.items.length }}件商品
            </div>
          </div>
          
          <div class="order-summary">
            <div class="total-amount">共{{ getTotalQuantity(order) }}件，实付 ¥{{ formatPrice(order.totalAmount || 0) }}</div>
            <div class="order-status">
              <span class="status-badge" :class="getOrderStatusClass(order.order_status || (order.is_completed ? 'completed' : 'pending'))">
                {{ getOrderStatusText(order.order_status || (order.is_completed ? 'completed' : 'pending')) }}
              </span>
              <button v-if="canBuyAgain(order)" class="btn btn-primary buy-again-btn" @click="buyAgain(order)">再次购买</button>
              <button class="btn btn-secondary" @click="showOrderDetail(order)">查看详情</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 无订单提示 -->
    <div v-else-if="!loading" class="alert alert-info">暂无订单记录</div>

    <!-- 订单详情弹窗 -->
    <div v-if="showDetail" class="modal-overlay" @click.self="closeDetail">
      <div class="modal-content card" style="max-width: 600px; max-height: 80vh; overflow-y: auto;">
        <div class="modal-header">
          <h3>订单详情</h3>
          <button class="close-btn" @click="closeDetail">×</button>
        </div>
        
        <div class="modal-body" v-if="selectedOrder">
          <!-- 订单基本信息 -->
          <div class="order-info-section">
            <div class="detail-item">
              <label>订单编号：</label>
              <span class="order-id">#{{ selectedOrder.id }}</span>
            </div>
            <div class="detail-item">
              <label>下单时间：</label>
              <span>{{ formatDateTime(selectedOrder.created_at) }}</span>
            </div>
            <div class="detail-item">
              <label>订单状态：</label>
              <span class="status-badge" :class="getOrderStatusClass(selectedOrder.order_status || (selectedOrder.is_completed ? 'completed' : 'pending'))">
                {{ getOrderStatusText(selectedOrder.order_status || (selectedOrder.is_completed ? 'completed' : 'pending')) }}
              </span>
            </div>
          </div>

          <!-- 收货信息 -->
          <div class="address-section">
            <h4>收货信息</h4>
            <div class="detail-item">
              <label>收货人：</label>
              <span>{{ selectedOrder.buyer_name }}</span>
            </div>
            <div class="detail-item">
              <label>联系电话：</label>
              <span>{{ selectedOrder.phone }}</span>
            </div>
            <div class="detail-item">
              <label>收货地址：</label>
              <span class="address-text">{{ selectedOrder.address }}</span>
            </div>
          </div>

          <!-- 商品清单 -->
          <div class="items-section">
            <h4>商品清单</h4>
            <div class="items-list detail-items-list">
              <div v-for="(item, index) in selectedOrder.items || [{productName: selectedOrder.product_name, price: selectedOrder.price, quantity: selectedOrder.quantity, totalAmount: selectedOrder.total_amount}]" :key="index" class="item-row">
                <div class="item-main">
                  <img
                    :src="getOrderItemImageUrl(item, index)"
                    :alt="item.productName || item.name || '商品图片'"
                    class="product-thumb detail-thumb"
                    referrerpolicy="no-referrer"
                  >
                  <div class="item-info">
                    <span class="item-name">{{ item.productName || item.name || '未知商品' }}</span>
                    <span v-if="item.sizeName" class="item-size">尺码: {{ item.sizeName }}<span v-if="item.sizeExtraPrice > 0"> (+¥{{ item.sizeExtraPrice.toFixed(2) }})</span></span>
                    <span class="item-price">¥{{ formatPrice(item.price || 0) }}</span>
                  </div>
                </div>
                <div class="item-side">
                  <div class="item-quantity">×{{ item.quantity || 1 }}</div>
                  <div class="item-total">¥{{ formatPrice(item.totalAmount || (item.price * (item.quantity || 1))) }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 订单金额 -->
          <div class="amount-section">
            <div class="amount-row">
              <span>商品金额</span>
              <span>¥{{ formatPrice(selectedOrder.totalAmount || 0) }}</span>
            </div>
            <div class="amount-row">
              <span>运费</span>
              <span>¥0.00</span>
            </div>
            <div class="amount-row total-row">
              <span>实付金额</span>
              <span class="total-amount">¥{{ formatPrice(selectedOrder.totalAmount || 0) }}</span>
            </div>
          </div>

          <!-- 订单备注 -->
          <div class="notes-section" v-if="selectedOrder.notes">
            <div class="detail-item">
              <label>订单备注：</label>
              <span>{{ selectedOrder.notes }}</span>
            </div>
          </div>
          
          <!-- 操作按钮 -->
          <div class="action-buttons">
            <!-- 支付按钮 -->
            <button v-if="canPayOrder(selectedOrder)" class="btn btn-primary" @click="showPaymentModal">立即支付</button>
            
            <!-- 取消订单按钮 -->
            <button v-if="canCancelOrder(selectedOrder)" class="btn btn-danger" @click="confirmCancelOrder">取消订单</button>
            
            <!-- 提交售后按钮 -->
            <button v-if="canApplyAfterSales(selectedOrder)" class="btn btn-primary" @click="openAfterSalesForm">提交售后</button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 确认取消订单弹窗 -->
    <div v-if="showCancelConfirm" class="modal-overlay" @click.self="closeCancelConfirm">
      <div class="modal-content card" style="max-width: 400px;">
        <div class="modal-header">
          <h3>确认取消订单</h3>
          <button class="close-btn" @click="closeCancelConfirm">×</button>
        </div>
        <div class="modal-body">
          <p>您确定要取消订单 #{{ selectedOrder?.id }} 吗？</p>
          <p style="color: #666; font-size: 14px; margin-top: 10px;">取消后订单将无法恢复。</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeCancelConfirm">取消</button>
          <button class="btn btn-danger" @click="cancelOrder">确认取消</button>
        </div>
      </div>
    </div>
    
    <!-- 支付弹窗 -->
    <div v-if="showPayment" class="modal-overlay" @click.self="closePaymentModal">
      <div class="modal-content card" style="max-width: 400px;">
        <div class="modal-header">
          <h3>订单支付</h3>
          <button class="close-btn" @click="closePaymentModal">×</button>
        </div>
        <div class="modal-body">
          <div class="payment-info">
            <p>订单号：#{{ selectedOrder?.id }}</p>
            <p class="total-amount">支付金额：¥{{ formatPrice(selectedOrder?.total_amount || 0) }}</p>
          </div>
          <div class="payment-methods">
            <div class="method-item" :class="{ active: paymentMethod === 'bankcard' }" @click="paymentMethod = 'bankcard'">
              <input id="bankcard" v-model="paymentMethod" type="radio" name="paymentMethod" value="bankcard">
              <label for="bankcard">
                <img src="/images/bankcard.png" alt="银行卡" class="method-icon">
                <span class="method-copy">
                  <strong>银行卡支付</strong>
                  <span>适合借记卡和储蓄卡快捷付款</span>
                </span>
              </label>
            </div>
            <div class="method-item" :class="{ active: paymentMethod === 'creditcard' }" @click="paymentMethod = 'creditcard'">
              <input id="creditcard" v-model="paymentMethod" type="radio" name="paymentMethod" value="creditcard">
              <label for="creditcard">
                <img src="/images/creditcard.png" alt="信用卡" class="method-icon">
                <span class="method-copy">
                  <strong>信用卡支付</strong>
                  <span>适合快速完成当前订单支付</span>
                </span>
              </label>
            </div>
            <div class="method-item" :class="{ active: paymentMethod === 'alipay' }" @click="paymentMethod = 'alipay'">
              <input id="alipay" v-model="paymentMethod" type="radio" name="paymentMethod" value="alipay">
              <label for="alipay">
                <img src="/images/alipay.png" alt="支付宝" class="method-icon">
                <span class="method-copy">
                  <strong>支付宝支付</strong>
                  <span>适合日常移动端扫码与钱包付款</span>
                </span>
              </label>
            </div>
            <div class="method-item" :class="{ active: paymentMethod === 'wechat' }" @click="paymentMethod = 'wechat'">
              <input id="wechat" v-model="paymentMethod" type="radio" name="paymentMethod" value="wechat">
              <label for="wechat">
                <img src="/images/wechat.png" alt="微信支付" class="method-icon">
                <span class="method-copy">
                  <strong>微信支付</strong>
                  <span>适合移动端快速确认并完成支付</span>
                </span>
              </label>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closePaymentModal">取消</button>
          <button class="btn btn-primary" @click="payOrder">立即支付</button>
        </div>
      </div>
    </div>
    
    <!-- 售后申请弹窗 -->
    <div v-if="showAfterSalesForm" class="modal-overlay" @click.self="closeAfterSalesForm">
      <div class="modal-content card" style="max-width: 500px;">
        <div class="modal-header">
          <h3>提交售后申请</h3>
          <button class="close-btn" @click="closeAfterSalesForm">×</button>
        </div>
        <div class="modal-body">
          <p style="margin-bottom: 20px;">订单 #{{ selectedOrder?.id }} - {{ selectedOrder?.product_name }}</p>

          <div class="form-group">
            <label for="after-sales-type">售后类型：</label>
            <select id="after-sales-type" v-model="afterSalesForm.type" class="form-control">
              <option value="">请选择</option>
              <option value="退款">退款</option>
              <option value="换货">换货</option>
              <option value="维修">维修</option>
            </select>
          </div>

          <div class="form-group">
            <label for="after-sales-reason">售后原因：</label>
            <textarea id="after-sales-reason" v-model="afterSalesForm.reason" class="form-control" rows="4" placeholder="请详细描述您遇到的问题..."></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeAfterSalesForm">取消</button>
          <button class="btn btn-primary" @click="submitAfterSales" :disabled="!afterSalesForm.type || !afterSalesForm.reason">提交申请</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomerOrders',
  data() {
    return {
      orders: [],
      loading: false,
      error: '',
      statusFilter: '',
      timeFilter: 'desc',
      showDetail: false,
      selectedOrder: null,
      showCancelConfirm: false,
      showPayment: false,
      paymentMethod: 'creditcard',
      productImageCache: {},
      showAfterSalesForm: false,
      afterSalesForm: {
        type: '',
        reason: ''
      }
    }
  },
  created() {
    this.fetchOrders()
  },
  methods: {
    async fetchOrders() {
      this.loading = true
      this.error = ''
      
      try {
        const customerUsername = localStorage.getItem('customerUsername')
        const encodedUsername = encodeURIComponent(customerUsername || '')
        
        // 构建查询参数
        const params = {}
        if (this.statusFilter) {
          params.status = this.statusFilter
        }
        if (this.timeFilter) {
          params.sort = this.timeFilter
        }
        
        const response = await this.$axios.get(`/customer-orders`, {
          headers: { 'X-Username': encodedUsername },
          params: params
        })
        
        this.orders = response.data.map(order => ({
          id: order.id,
          items: order.items || [],
          product_name: order.items?.[0]?.productName || '未知商品',
          price: order.items?.[0]?.price || 0,
          quantity: order.items?.[0]?.quantity || 1,
          totalAmount: order.totalAmount || 0,
          buyer_name: order.buyerName,
          phone: order.phone,
          address: order.address,
          notes: order.notes,
          order_status: order.status,
          is_completed: order.status === 4,
          created_at: order.createdAt,
          shop_name: '店铺名称',
          sellerId: order.sellerId || order.product?.seller?.id || 1 // 添加sellerId字段
        }))
        await this.hydrateOrderImages(this.orders)
      } catch (err) {
        this.error = '获取订单列表失败'
        console.error(err)
      } finally {
        this.loading = false
      }
    },
    
    async showOrderDetail(order) {
      await this.hydrateOrderImages([order])
      this.selectedOrder = order
      this.showDetail = true
    },
    
    closeDetail() {
      this.showDetail = false
      this.selectedOrder = null
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN')
    },
    
    formatDateTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN')
    },
    
    formatPrice(price) {
      if (typeof price === 'string') {
        price = parseFloat(price)
      }
      return price.toFixed(2)
    },

    normalizeImageUrl(imageUrl) {
      if (!imageUrl || typeof imageUrl !== 'string') return ''
      if (imageUrl.startsWith('http')) return imageUrl
      if (imageUrl.startsWith('/')) return imageUrl
      return `/api/files/images/${imageUrl}`
    },

    async hydrateOrderImages(orders) {
      const productIds = [
        ...new Set(
          orders.flatMap(order => (order.items || []))
            .map(item => item?.productId)
            .filter(Boolean)
        )
      ]

      await Promise.all(productIds.map(async (productId) => {
        if (this.productImageCache[productId]) return
        try {
          const response = await this.$axios.get(`/products/${productId}`)
          const imageUrl = this.normalizeImageUrl(response.data?.imageUrl || '')
          if (imageUrl) {
            this.productImageCache[productId] = imageUrl
          }
        } catch (error) {
          console.error(`获取商品 ${productId} 图片失败:`, error)
        }
      }))

      orders.forEach(order => {
        (order.items || []).forEach(item => {
          const current = this.normalizeImageUrl(item?.imageUrl || item?.productImage || item?.mainImage || item?.image || item?.product?.imageUrl || item?.product?.mainImage || '')
          item.imageUrl = current || this.productImageCache[item?.productId] || ''
        })
      })
    },

    getOrderItemImageUrl(item, index = 0) {
      const candidate = this.normalizeImageUrl(
        item?.imageUrl ||
        item?.productImage ||
        item?.mainImage ||
        item?.image ||
        item?.product?.imageUrl ||
        item?.product?.mainImage ||
        this.productImageCache[item?.productId] ||
        ''
      )

      if (candidate) {
        return candidate
      }

      const seed = item?.productId || item?.id || `${this.selectedOrder?.id || 'order'}-${index}`
      return `https://picsum.photos/seed/order-${seed}/120/120.jpg`
    },

    getOrderStatusClass(status) {
      // 处理数字状态
      const statusNum = parseInt(status);
      if (!isNaN(statusNum)) {
        switch (statusNum) {
          case 0:
            return 'status-customer-ordered';
          case 1:
            return 'status-merchant-confirmed';
          case 2:
            return 'status-ready-for-shipment';
          case 3:
            return 'status-shipping';
          case 4:
            return 'status-completed';
          case 5:
            return 'status-failed';
          default:
            return '';
        }
      }
      // 兼容旧的文本状态
      switch (status) {
        case 'COMPLETED':
        case 'completed':
          return 'status-completed';
        case 'PENDING':
        case 'pending':
          return 'status-customer-ordered';
        case 'CANCELLED':
        case 'cancelled':
          return 'status-cancelled';
        case 'FAILED':
        case 'failed':
          return 'status-failed';
        default:
          return '';
      }
    },
    
    getOrderStatusText(status) {
      // 处理数字状态
      const statusNum = parseInt(status);
      if (!isNaN(statusNum)) {
        switch (statusNum) {
          case 0:
            return '客户下单（未支付）';
          case 1:
            return '商家确认（已支付）';
          case 2:
            return '备货完成（已支付）';
          case 3:
            return '开始发货（已支付）';
          case 4:
            return '交易完成（已支付）';
          case 5:
            return '交易失败';
          default:
            return '未知';
        }
      }
      // 兼容旧的文本状态
      switch (status) {
        case 'COMPLETED':
        case 'completed':
          return '交易完成';
        case 'PENDING':
        case 'pending':
          return '客户下单';
        case 'CANCELLED':
        case 'cancelled':
          return '已取消';
        case 'FAILED':
        case 'failed':
          return '交易失败';
        default:
          return status || '未知';
      }
    },
    
    // 判断订单是否可以取消（客户规则：开始发货前，即状态<3）
    canCancelOrder(order) {
      const status = parseInt(order.order_status);
      // 不是NaN且状态小于3且不是终态
      return !isNaN(status) && status < 3 && status !== 4 && status !== 5;
    },
    
    // 判断订单是否可以支付
    canPayOrder(order) {
      const status = parseInt(order.order_status);
      // 订单状态为0（客户下单）时可以支付
      return !isNaN(status) && status === 0;
    },
    
    // 计算订单总数量
    getTotalQuantity(order) {
      if (order.items && order.items.length > 0) {
        return order.items.reduce((sum, item) => sum + (item.quantity || 1), 0);
      }
      return order.quantity || 1;
    },
    
    // 显示支付弹窗
    showPaymentModal() {
      this.showPayment = true;
    },
    
    // 关闭支付弹窗
    closePaymentModal() {
      this.showPayment = false;
    },
    
    // 执行支付操作
    async payOrder() {
      if (!this.selectedOrder) return;
      
      try {
        const customerUsername = localStorage.getItem('customerUsername');
        const encodedUsername = encodeURIComponent(customerUsername || '');
        
        await this.$axios.post(`/customer-orders/${this.selectedOrder.id}/pay`, {}, {
          headers: { 'X-Username': encodedUsername }
        });
        
        this.closePaymentModal();
        this.fetchOrders();
        alert('支付成功！');
        this.closeDetail();
      } catch (err) {
        console.error('支付失败', err);
        alert(err.response?.data?.message || '支付失败，请稍后重试');
      }
    },
    
    // 显示确认取消订单对话框
    confirmCancelOrder() {
      this.showCancelConfirm = true;
    },
    
    // 关闭确认取消订单对话框
    closeCancelConfirm() {
      this.showCancelConfirm = false;
    },
    
    // 执行取消订单操作
    async cancelOrder() {
      if (!this.selectedOrder) return;
      
      try {
        const customerUsername = localStorage.getItem('customerUsername');
        const encodedUsername = encodeURIComponent(customerUsername || '');
        
        await this.$axios.put(`/customer-orders/${this.selectedOrder.id}/cancel`, {}, {
          headers: { 'X-Username': encodedUsername }
        });
        
        this.closeCancelConfirm();
        this.fetchOrders();
        alert('订单取消成功！');
        this.closeDetail();
      } catch (err) {
        console.error('取消订单失败', err);
        alert(err.response?.data?.message || '取消订单失败，请稍后重试');
      }
    },
    
    // 判断订单是否可以提交售后（已完成的订单）
    canApplyAfterSales(order) {
      const status = parseInt(order.order_status);
      return !isNaN(status) && status === 4; // 4表示交易完成
    },
    
    // 判断订单是否可以再次购买（已完成的订单）
    canBuyAgain(order) {
      const status = parseInt(order.order_status);
      return !isNaN(status) && status === 4; // 4表示交易完成
    },
    
    // 再次购买 - 跳转到商品详情页使用统一购买表单
    buyAgain(order) {
      // 获取订单中的第一个商品ID
      const items = order.items || [];
      let productId = null;
      
      if (items.length > 0 && items[0].productId) {
        productId = items[0].productId;
      } else if (order.product_id) {
        // 兼容旧格式
        productId = order.product_id;
      }
      
      if (productId) {
        // 跳转到商品详情页面
        this.$router.push(`/product/${productId}`);
      } else {
        console.error('无法获取商品ID');
        window.showToast('无法获取商品信息', 'error');
      }
    },
    
    // 显示Toast提示
    showToast(message) {
      console.log('showToast called with message:', message);
      
      // 创建Toast元素
      const toast = document.createElement('div');
      toast.className = 'toast-notification';
      toast.textContent = message;
      document.body.appendChild(toast);
      
      console.log('Toast element added to DOM');
      
      // 显示Toast
      setTimeout(() => {
        toast.classList.add('show');
        console.log('Toast shown');
      }, 10);
      
      // 3秒后移除Toast
      setTimeout(() => {
        toast.classList.remove('show');
        setTimeout(() => {
          document.body.removeChild(toast);
          console.log('Toast removed from DOM');
        }, 300);
      }, 3000);
    },
    
    // 打开售后申请表单
    openAfterSalesForm() {
      // 重置表单
      this.afterSalesForm = {
        type: '',
        reason: ''
      };
      this.showAfterSalesForm = true;
    },
    
    // 关闭售后申请表单
    closeAfterSalesForm() {
      this.showAfterSalesForm = false;
    },
    
    // 提交售后申请
    async submitAfterSales() {
      if (!this.selectedOrder || !this.afterSalesForm.type || !this.afterSalesForm.reason) return;
      
      try {
        // 从localStorage获取当前登录用户名
        const customerUsername = localStorage.getItem('customerUsername');
        
        // 构建售后申请数据
        const afterSalesData = {
          buyerId: this.selectedOrder.id, // 使用订单ID作为buyerId        
          customerName: customerUsername, // 使用当前登录用户的用户名
          productId: this.selectedOrder.product_id || 1, // 使用订单中的商品ID
          productName: this.selectedOrder.product_name, // 使用订单中的商品名称
          type: this.afterSalesForm.type,
          reason: this.afterSalesForm.reason,
          status: '待处理',
          sellerId: this.selectedOrder.sellerId || 1 // 传递卖家ID
        };
        
        // 调试日志
        console.log('提交售后申请数据:', afterSalesData);
        
        // 构建请求头
        const headers = {
          'X-Username': customerUsername
        };
        
        // 调用后端API创建售后申请
        const response = await this.$axios.post('/after-sales', afterSalesData, {
          headers: headers
        });
        
        // 调试日志
        console.log('售后申请提交成功:', response.data);
        
        // 使用全局Toast显示成功提示（不会因页面切换消失）
        window.showToast('售后申请提交成功！', 'success');
        
        // 延迟关闭弹窗，确保Toast有足够时间显示
        setTimeout(() => {
          // 关闭售后申请表单
          this.closeAfterSalesForm();
          // 关闭订单详情
          this.closeDetail();
        }, 100);
      } catch (err) {
        console.error('提交售后申请失败', err);
        // 使用全局Toast显示失败提示（不会因页面切换消失）
        window.showToast(err.response?.data?.message || '提交售后申请失败，请稍后重试', 'error');
      }
    },
  }
}
</script>

<style scoped>
.order-list {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px 0;
}

.after-sales-entry {
  margin: 8px 0 16px;
}

.filter-section {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-item label {
  font-weight: 500;
}

.filter-item select {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.orders-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.order-card {
  padding: 20px;
  border: 1px solid #eee;
  border-radius: 8px;
  background-color: white;
  transition: box-shadow 0.3s;
}

.order-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.order-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.order-id {
  font-weight: 600;
  color: #333;
}

.order-time {
  color: #666;
  font-size: 14px;
}

.order-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.product-info {
  flex: 1;
}

.product-name {
  font-weight: 500;
  color: #333;
  margin-right: 15px;
}

.product-size {
  font-size: 0.8rem;
  color: #999;
  margin-right: 10px;
  background: #f5f5f5;
  padding: 1px 6px;
  border-radius: 3px;
}

.item-size {
  display: block;
  font-size: 0.8rem;
  color: #E02E24;
  margin-top: 2px;
}

.order-amount {
  font-weight: 600;
  color: #E02E24;
  font-size: 18px;
}

.order-status {
  display: flex;
  align-items: center;
  gap: 15px;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-customer-ordered {
  background-color: #fff3cd;
  color: #856404;
}

.status-merchant-confirmed {
  background-color: #d1ecf1;
  color: #0c5460;
}

.status-ready-for-shipment {
  background-color: #e2e3e5;
  color: #383d41;
}

.status-shipping {
  background-color: #cce7ff;
  color: #004085;
}

.status-completed {
  background-color: #d4edda;
  color: #155724;
}

.status-failed {
  background-color: #f8d7da;
  color: #721c24;
}

.status-cancelled {
  background-color: #f8d7da;
  color: #721c24;
}

.btn {
  padding: 0.5rem 1.0rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 0.8rem;
  margin-bottom: 0.8rem;
  display: inline-block;
  font-size: 14px;
}

.btn-secondary {
  background-color: #6c757d;
}

.btn-danger {
  background-color: #dc3545;
}

.btn-primary {
  background-color: #007bff;
}

.payment-info {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.payment-info p {
  margin-bottom: 8px;
  color: #666;
}

.payment-info .total-amount {
  font-size: 20px;
  font-weight: 600;
  color: #E02E24;
  margin-top: 10px;
}

.payment-methods {
  margin-bottom: 20px;
  display: grid;
  gap: 0.8rem;
}

.payment-methods .method-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 0;
  padding: 0.95rem 1rem;
  border: 1px solid var(--color-border);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.9);
  transition:
    border-color var(--duration-fast) var(--ease-standard),
    background var(--duration-fast) var(--ease-standard),
    transform var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);
}

.payment-methods .method-item:hover,
.payment-methods .method-item.active {
  border-color: rgba(0, 113, 227, 0.22);
  background: rgba(248, 250, 252, 0.96);
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.05);
}

.payment-methods .method-item input {
  accent-color: #111113;
}

.payment-methods .method-item label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  cursor: pointer;
}

.method-icon {
  width: 30px;
  height: 30px;
  margin-right: 0;
  border-radius: 10px;
  object-fit: contain;
}

.loading {
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: #f5f5f5;
}

.modal-body {
  padding: 20px;
}

.detail-item {
  display: flex;
  margin-bottom: 15px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-item label {
  width: 100px;
  font-weight: 500;
  color: #666;
}

.detail-item span {
  flex: 1;
  color: #333;
}

/* 模态框底部按钮样式 */
.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 15px;
}

.modal-footer .btn {
  margin-right: 0;
  margin-bottom: 0;
}

/* 表单样式 */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #666;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* 操作按钮样式 */
.action-buttons {
  margin-top: 20px;
  display: flex;
  gap: 10px;
}

.action-buttons .btn {
  margin-right: 0;
  margin-bottom: 0;
}

/* Toast提示样式 */
.toast-notification {
  position: fixed;
  top: 80px;
  right: 20px;
  padding: 16px 28px;
  background-color: #111113;
  color: white;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  z-index: 99999;
  opacity: 0;
  transform: translateX(100%);
  transition: opacity 0.3s ease, transform 0.3s ease;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  min-width: 200px;
  text-align: center;
}

.toast-notification.show {
  opacity: 1;
  transform: translateX(0);
}

.order-list > h2 {
  margin-bottom: 1rem;
  color: var(--color-text);
  font-size: clamp(2rem, 3vw, 2.6rem);
  letter-spacing: -0.04em;
}

.after-sales-entry,
.filter-section,
.order-card,
.modal-content,
.toast-notification {
  border: 1px solid rgba(255, 255, 255, 0.72);
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: var(--shadow-soft);
}

.filter-section {
  border-radius: 28px;
  padding: 1rem 1.2rem;
}

.filter-item label,
.order-time,
.detail-item label,
.address-text,
.item-price {
  color: var(--color-text-soft);
}

.filter-item select,
.form-control {
  border-radius: 999px;
  border: 1px solid var(--color-border);
  background: rgba(255, 255, 255, 0.92);
}

.orders-container {
  gap: 1rem;
}

.order-card {
  border-radius: 26px;
  overflow: hidden;
}

.order-header,
.amount-section,
.items-list {
  background: transparent;
}

.order-id,
.product-name,
.item-name,
.total-amount,
.modal-header h3,
.detail-item span {
  color: var(--color-text);
}

.status-badge,
.buy-again-btn,
.action-buttons .btn {
  border-radius: 999px;
}

.modal-content {
  border-radius: 28px;
}

.modal-body {
  color: var(--color-text-soft);
}

.toast-notification {
  color: var(--color-text);
  border-radius: 18px;
}

.method-copy {
  display: grid;
  gap: 0.2rem;
}

.method-copy strong {
  color: var(--color-text);
  font-size: 0.96rem;
}

.method-copy span {
  color: var(--color-text-soft);
  font-size: 0.86rem;
}

.order-list {
  background:
    radial-gradient(circle at top left, rgba(191, 219, 254, 0.42), transparent 30%),
    linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
  min-height: calc(100vh - 80px);
  padding: 1.25rem 0 2rem;
}

.orders-container {
  display: grid;
  gap: 1.1rem;
}

.order-card {
  position: relative;
  animation: orderCardRise 0.55s ease both;
  padding: 1.2rem 1.25rem 1.35rem;
  border: 1px solid rgba(255, 255, 255, 0.76);
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08);
}

.order-card:nth-child(2) {
  animation-delay: 0.05s;
}

.order-card:nth-child(3) {
  animation-delay: 0.1s;
}

.order-content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 1.15rem;
  align-items: start;
}

.products-info {
  min-width: 0;
  padding: 1rem 1.05rem;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.9), rgba(255, 255, 255, 0.75));
  border: 1px solid rgba(15, 23, 42, 0.06);
}

.product-row {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr) auto auto;
  gap: 0.85rem;
  align-items: center;
  padding: 0.7rem 0;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

.product-row:first-child {
  border-top: 0;
  padding-top: 0;
}

.product-thumb {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  object-fit: cover;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
}

.detail-thumb {
  width: 56px;
  height: 56px;
  border-radius: 16px;
}

.product-copy,
.item-info {
  min-width: 0;
  display: grid;
  gap: 0.28rem;
}

.product-copy {
  align-content: center;
}

.product-name,
.address-text,
.detail-item span,
.item-name {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.order-summary {
  display: grid;
  gap: 0.85rem;
  align-content: start;
  justify-items: end;
  padding: 1rem 1.1rem;
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.92), rgba(255, 255, 255, 0.82));
  border: 1px solid rgba(15, 23, 42, 0.06);
}

.order-status {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn,
.status-badge,
.payment-methods .method-item,
.modal-footer .btn,
.action-buttons .btn {
  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease,
    border-color 0.22s ease,
    background-color 0.22s ease;
}

.btn {
  border-radius: 999px;
  background: linear-gradient(135deg, #111113, #2c2c2e);
  color: #fff;
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.14);
}

.btn-secondary {
  background: rgba(241, 245, 249, 0.96);
  color: var(--color-text);
  border-color: rgba(148, 163, 184, 0.26);
}

.btn-danger {
  background: rgba(153, 27, 27, 0.06);
  color: #991b1b;
  border-color: rgba(153, 27, 27, 0.18);
}

.btn-primary {
  background: linear-gradient(135deg, #111113, #2c2c2e);
}

.payment-info,
.address-section,
.items-section,
.amount-section,
.notes-section {
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.92), rgba(255, 255, 255, 0.82));
  border: 1px solid rgba(15, 23, 42, 0.06);
  padding: 1rem 1.05rem;
}

.item-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.9rem;
  align-items: center;
  padding: 0.85rem 0;
}

.detail-items-list {
  display: grid;
  gap: 0.8rem;
}

.detail-items-list .item-row {
  padding: 0.95rem 1rem;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.06);
}

.item-main {
  min-width: 0;
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  gap: 0.85rem;
  align-items: center;
}

.item-side {
  display: grid;
  gap: 0.3rem;
  justify-items: end;
  text-align: right;
  min-width: 92px;
}

.order-card:hover,
.detail-items-list .item-row:hover {
  transform: scale(1.01);
  border-color: rgba(0, 113, 227, 0.18);
  box-shadow: 0 18px 34px rgba(15, 23, 42, 0.1);
}

.detail-item {
  align-items: flex-start;
  gap: 0.85rem;
}

.detail-item label {
  width: 118px;
  flex: 0 0 118px;
}

.amount-section {
  display: grid;
  gap: 0.55rem;
}

.amount-row {
  margin-bottom: 0;
}

@keyframes orderCardRise {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 900px) {
  .order-content,
  .product-row {
    grid-template-columns: 1fr;
  }

  .item-row {
    grid-template-columns: 1fr;
  }

  .item-main {
    grid-template-columns: 1fr;
  }

  .detail-thumb {
    width: 72px;
    height: 72px;
  }

  .item-side {
    justify-items: start;
    text-align: left;
  }

  .order-summary,
  .order-status {
    justify-items: start;
    justify-content: flex-start;
  }
}
</style>